import { ReactElement, ReactNode } from "react"
import "./todoExplainer.scss"

export type ExplainerListVariant = "numbered" | "bullet" | "checklist"
export type ExplainerCalloutVariant = "info" | "warning" | "success"

/**
 * Callbacks a todo explainer component is invoked with. Builders in
 * todoExplainerContent.tsx decide for themselves which of these to wire up to
 * buttons/links and how to lay out the rest of the page — these are just the
 * navigation primitives TodoExplainerPage resolves from Redux.
 */
export interface TodoExplainerActionProps {
  onProceed: () => void
  onSecondary?: () => void
  onCancel: () => void
}

export type TodoExplainerComponent = (props: TodoExplainerActionProps) => ReactElement

export function ExplainerLayout({
  heading,
  subheading,
  children,
}: {
  heading: string
  subheading?: string
  children: ReactNode
}) {
  return (
    <div className="todo-explainer">
      <h2 className="todo-explainer__heading">{heading}</h2>
      {subheading && <p className="todo-explainer__subheading">{subheading}</p>}
      {children}
    </div>
  )
}

export function ExplainerSection({ heading, children }: { heading?: string; children: ReactNode }) {
  return (
    <section className="todo-explainer__section">
      {heading && <h5>{heading}</h5>}
      {children}
    </section>
  )
}

export function ExplainerList({ variant, items }: { variant: ExplainerListVariant; items: ReactNode[] }) {
  if (variant === "numbered") {
    return (
      <ol className="todo-explainer__list todo-explainer__list--numbered">
        {items.map((it, i) => (
          <li key={i}>{it}</li>
        ))}
      </ol>
    )
  }
  if (variant === "checklist") {
    return (
      <ul className="todo-explainer__list todo-explainer__list--checklist">
        {items.map((it, i) => (
          <li key={i}>
            <i className="material-icons">check_box_outline_blank</i>
            <span>{it}</span>
          </li>
        ))}
      </ul>
    )
  }
  return (
    <ul className="todo-explainer__list">
      {items.map((it, i) => (
        <li key={i}>{it}</li>
      ))}
    </ul>
  )
}

export function ExplainerScript({ children }: { children: ReactNode }) {
  return <blockquote className="todo-explainer__script">{children}</blockquote>
}

export function ExplainerCallout({ variant, body }: { variant: ExplainerCalloutVariant; body: ReactNode }) {
  const cls = variant === "info" ? "alert-info" : variant === "warning" ? "alert-warning" : "alert-success"
  return <div className={`alert ${cls} todo-explainer__callout`}>{body}</div>
}

export function ExplainerActions({
  primaryLabel,
  onProceed,
  secondaryLabel,
  onSecondary,
  cancelLabel = "Not now",
  onCancel,
}: {
  primaryLabel: string
  onProceed: () => void
  secondaryLabel?: string
  onSecondary?: () => void
  cancelLabel?: string
  onCancel: () => void
}) {
  return (
    <div className="todo-explainer__actions">
      <button type="button" className="btn btn-primary btn-lg" onClick={onProceed}>
        {primaryLabel}
      </button>
      {secondaryLabel && onSecondary && (
        <button type="button" className="btn btn-outline-primary" onClick={onSecondary}>
          {secondaryLabel}
        </button>
      )}
      <button type="button" className="btn btn-link" onClick={onCancel}>
        {cancelLabel}
      </button>
    </div>
  )
}
